import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Pencil, Send, Trash2, FilePenLine, ShieldCheck, MapPin, History, MessageSquare, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { GpsInfo } from "../../lib/field";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { MediaThumb, StatusStamp } from "../../components/activity/ActivityCard";
import { ACTIVITY_TYPES, EXPENSE_CATEGORIES, EVIDENCE, EDITABLE, ROLE_HOME, STATUS_LABELS, formatDay, formatDate, formatRupiah, productionText, isManagerRole } from "../../lib/labels";

const Block = ({ q, children, testid }) => (
  <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6" data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800">{q}</p>
    <div className="mt-2 text-sm text-slate-700 space-y-1.5">{children}</div>
  </section>
);
const Row = ({ k, v }) => (v || v === 0 ? <p><span className="text-slate-500">{k}: </span><span className="text-slate-900">{v}</span></p> : null);

const ReviewPanel = ({ a, reload }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/activities/${a.id}/review`, { decision, note });
      toast.success(STATUS_LABELS[decision]);
      setNote("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!["SUBMITTED", "UNDER_REVIEW"].includes(a.status)) return null;
  return (
    <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6 space-y-3" data-testid="review-panel">
      <p className="font-semibold text-blue-900">Pemantauan PT SJI</p>
      <p className="text-xs text-blue-900/80">Kelompok tani / koperasi bertanggung jawab atas laporan ini. Jika ada yang tidak sesuai, kirim saran di bagian "Saran & Tanggapan".</p>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan pemantauan (opsional)" className="bg-white" data-testid="review-note-input" />
      <div className="grid grid-cols-2 gap-2">
        {a.status === "SUBMITTED" && <Button variant="outline" disabled={busy} onClick={() => act("UNDER_REVIEW")} data-testid="review-start-btn">Mulai Tinjau</Button>}
        <Button disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" onClick={() => act("VERIFIED")} data-testid="review-verify-btn">Verifikasi</Button>
      </div>
    </section>
  );
};

const AdviceThread = ({ a, user, reload }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const canWrite = a.status !== "DRAFT" && ["SUPER_ADMIN", "FARMER_GROUP", "INDIVIDUAL_MANAGER"].includes(user.role);
  const list = a.advice || [];
  if (!list.length && !canWrite) return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/activities/${a.id}/advice`, { text });
      setText("");
      toast.success(data.kind === "SARAN" ? `Saran terkirim${data.emailed ? ` · email ke ${data.emailed} akun kelompok tani` : ""}` : "Tanggapan terkirim");
      reload();
    }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Block q="Saran PT SJI & Tanggapan Kelompok Tani" testid="advice-thread">
      {!list.length && <p className="text-slate-500">Belum ada saran.</p>}
      {list.map((n) => (
        <div key={n.id} className={`rounded-lg border p-3 ${n.kind === "SARAN" ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`} data-testid={`advice-${n.id}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{n.kind === "SARAN" ? "Saran PT SJI" : `Tanggapan ${a.farmerGroupName || "Kelompok Tani"}`} · {formatDate(n.at)}</p>
          <p className="text-slate-900 whitespace-pre-line">{n.text}</p>
        </div>
      ))}
      {canWrite && <div className="pt-2 space-y-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder={user.role === "SUPER_ADMIN" ? "Tulis saran untuk kelompok tani / koperasi" : "Tulis tanggapan atau tindak lanjut"} className="bg-white" data-testid="advice-input" />
        <Button disabled={busy || !text.trim()} onClick={send} className="h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="advice-send-btn"><MessageSquare className="h-4 w-4 mr-1.5" />{user.role === "SUPER_ADMIN" ? "Kirim Saran" : "Kirim Tanggapan"}</Button>
      </div>}
    </Block>
  );
};

const CaptionEdit = ({ m, onSaved }) => {
  const [v, setV] = useState(m.caption || "");
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await api.patch(`/media/${m.id}/caption`, { caption: v }); toast.success("Catatan foto disimpan"); setOpen(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-800" data-testid={`caption-edit-${m.id}`}><Pencil className="h-3 w-3" />{m.caption ? "Ubah catatan" : "Tambah catatan"}</button>;
  return (
    <div className="mt-1 flex gap-1">
      <input value={v} maxLength={300} onChange={(e) => setV(e.target.value)} className="min-w-0 flex-1 h-9 rounded border px-2 text-xs" data-testid={`caption-input-${m.id}`} />
      <button type="button" onClick={save} className="h-9 w-9 shrink-0 rounded bg-emerald-800 text-white grid place-items-center" aria-label="Simpan" data-testid={`caption-save-${m.id}`}><Check className="h-4 w-4" /></button>
    </div>
  );
};

const waNumber = (p) => {
  const d = (p || "").replace(/\D/g, "");
  return d.startsWith("0") ? `62${d.slice(1)}` : d;
};

const waText = (a) => {
  const photos = a.media.filter((m) => m.type === "PHOTO").length;
  const videos = a.media.filter((m) => m.type === "VIDEO").length;
  return [
    `*Laporan Kegiatan Kebun Kopi ${a.projectCode || ""}*`,
    `${ACTIVITY_TYPES[a.activityType] || a.activityType} · ${formatDay(a.activityDate)}`,
    `Pengelola: ${a.farmerGroupName || "Kelompok Tani"}`,
    a.workResult ? `Hasil: ${a.workResult}` : null,
    `Bukti: ${photos} foto, ${videos} video${a.gps ? ", GPS terekam" : ""}`,
    "",
    `Lihat laporan lengkap: ${window.location.origin}/buka/kegiatan/${a.id}`,
  ].filter((x) => x !== null).join("\n");
};

const ShareWA = ({ a }) => {
  const [targets, setTargets] = useState(null);
  useEffect(() => { api.get(`/activities/${a.id}/share-targets`).then((r) => setTargets(r.data)).catch(() => setTargets([])); }, [a.id]);
  if (a.status === "DRAFT" || !targets) return null;
  const text = encodeURIComponent(waText(a));
  const cls = "inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-sm font-semibold";
  return (
    <section className="bg-white border rounded-xl p-4 space-y-2" data-testid="share-wa">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800">Bagikan ke WhatsApp</p>
      <div className="flex flex-wrap gap-2">
        {targets.filter((t) => waNumber(t.phone)).map((t) => (
          <a key={t.id} href={`https://wa.me/${waNumber(t.phone)}?text=${text}`} target="_blank" rel="noopener noreferrer" className={`${cls} bg-[#128C7E] text-white`} data-testid={`share-wa-${t.id}`}>
            <MessageCircle className="h-4 w-4" />Kirim ke {t.role} {t.name}
          </a>
        ))}
        <a href={`https://wa.me/?text=${text}`} target="_blank" rel="noopener noreferrer" className={`${cls} border text-emerald-900`} data-testid="share-wa-any"><MessageCircle className="h-4 w-4" />Pilih kontak WhatsApp</a>
      </div>
      {targets.some((t) => !waNumber(t.phone)) && <p className="text-xs text-slate-500" data-testid="share-wa-missing">Nomor WhatsApp {targets.filter((t) => !waNumber(t.phone)).map((t) => t.name).join(", ")} belum diisi PT SJI. Gunakan "Pilih kontak WhatsApp".</p>}
      <p className="text-xs text-slate-500">Penerima membuka link dari pesan, masuk dengan akunnya, lalu langsung melihat laporan ini.</p>
    </section>
  );
};

const FarmerActions = ({ a, base, reload }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const openCorrection = a.corrections?.some((c) => !["VERIFIED", "REJECTED"].includes(c.status));
  return (
    <div className="flex flex-wrap gap-2">
      {EDITABLE.includes(a.status) && <Button asChild variant="outline" className="h-11" data-testid="activity-edit-btn"><Link to={`${base}/kegiatan/${a.id}/ubah`}><Pencil className="h-4 w-4 mr-1.5" /> Ubah</Link></Button>}
      {EDITABLE.includes(a.status) && <Button disabled={busy} className="h-11 bg-emerald-800 hover:bg-emerald-900" onClick={() => run(() => api.post(`/activities/${a.id}/submit`).then(reload), "Dikirim ke PT SJI")} data-testid="activity-submit-detail-btn"><Send className="h-4 w-4 mr-1.5" /> Kirim ke PT SJI</Button>}
      {a.status === "DRAFT" && !a.submittedAt && <Button disabled={busy} variant="outline" className="h-11 text-red-700" onClick={() => run(() => api.delete(`/activities/${a.id}`).then(() => navigate(`${base}/kegiatan`)), "Draf dihapus")} data-testid="activity-delete-draft-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Hapus Draf</Button>}
      {["SUBMITTED", "UNDER_REVIEW", "VERIFIED"].includes(a.status) && !a.supersededBy && !openCorrection && <Button asChild variant="outline" className="h-11" data-testid="activity-correction-btn"><Link to={`${base}/kegiatan/baru?correctionOf=${a.id}`}><FilePenLine className="h-4 w-4 mr-1.5" /> Ajukan Koreksi</Link></Button>}
    </div>
  );
};

export default function ActivityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const base = ROLE_HOME[user.role];
  const [a, setA] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => { api.get(`/activities/${id}`).then((r) => setA(r.data)).catch((e) => setError(formatApiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <p className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700" data-testid="activity-error">{error}</p>;
  if (!a) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;
  const groups = Object.entries(EVIDENCE).map(([cat, label]) => [label, a.media.filter((m) => (m.category || "GENERAL") === cat)]).filter(([, l]) => l.length);
  return (
    <div className="max-w-4xl space-y-5" data-testid="activity-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{formatDay(a.activityDate)} · {a.activityTime} · <Link to={`${base}/proyek/${a.projectId}`} className="font-mono text-emerald-800 font-semibold" data-testid="activity-project-link">{a.projectCode}</Link></p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 uppercase" data-testid="page-title">{ACTIVITY_TYPES[a.activityType]} {a.projectCode && `Kebun ${a.projectCode}`}</h1>
        </div>
        <StatusStamp value={a.status} testid="activity-detail-status" />
      </div>
      {a.supersededBy && <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">Data ini telah dikoreksi. <Link className="font-semibold underline" to={`${base}/kegiatan/${a.supersededBy}`}>Lihat koreksi terverifikasi</Link></p>}
      {a.original && <p className="rounded-lg bg-slate-50 border p-3 text-sm">Koreksi atas kegiatan <Link className="font-semibold underline text-emerald-800" to={`${base}/kegiatan/${a.original.id}`} data-testid="original-link">{formatDay(a.original.activityDate)}</Link> (data asli tetap tersimpan).</p>}
      {isManagerRole(user.role) && <FarmerActions a={a} base={base} reload={load} />}
      {isManagerRole(user.role) && <ShareWA a={a} />}
      {user.role === "SUPER_ADMIN" && <ReviewPanel a={a} reload={load} />}
      <p className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900" data-testid="responsibility-note">Penanggung jawab kegiatan, foto, catatan & laporan: <b>{a.farmerGroupName || "Kelompok Tani / Koperasi"}</b>. PT SJI memantau dan memberi saran.</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Block q="Apa yang dikerjakan" testid="block-what">
          <p className="text-slate-900 whitespace-pre-line">{a.description}</p>
          <Row k="Hasil kerja" v={a.workResult} /><Row k="Jumlah pekerja" v={a.workers} />
          <Row k="Luas/Jumlah dikerjakan" v={a.quantityWorked != null ? `${a.quantityWorked} ${a.quantityUnit || ""}` : null} />
          {a.fertilizer && <>
            <Row k="Pupuk" v={[a.fertilizer.name, a.fertilizer.type].filter(Boolean).join(" · ")} />
            <Row k="Jumlah pupuk" v={a.fertilizer.quantity != null ? `${a.fertilizer.quantity} ${a.fertilizer.unit}` : null} />
            <Row k="Pohon dipupuk" v={a.fertilizer.treesTreated} /><Row k="Luas dipupuk" v={a.fertilizer.areaTreated != null ? `${a.fertilizer.areaTreated} ha` : null} />
          </>}
          {a.production && <Row k="Produksi kopi" v={productionText(a.production)} />}
          <Row k="Catatan" v={a.notes} />
        </Block>
        <Block q="Berapa & untuk apa uang digunakan" testid="block-cost">
          {a.expenses.length ? (
            <table className="w-full text-sm" data-testid="activity-expense-table"><tbody>
              {a.expenses.map((e) => <tr key={e.id} className="border-b border-slate-100"><td className="py-1.5 pr-2">{e.description}<span className="block text-xs text-slate-500">{EXPENSE_CATEGORIES[e.category]}</span></td><td className="py-1.5 text-right whitespace-nowrap">{formatRupiah(e.amount)}</td></tr>)}
              <tr><td className="pt-2 font-bold">TOTAL</td><td className="pt-2 text-right font-bold" data-testid="activity-total-cost">{formatRupiah(a.totalCost)}</td></tr>
            </tbody></table>
          ) : <p>Tanpa biaya.</p>}
        </Block>
        <Block q="Kapan & di mana" testid="block-where">
          <Row k="Tanggal" v={`${formatDay(a.activityDate)} pukul ${a.activityTime}`} />
          <Row k="Kebun" v={`${a.projectCode} - ${a.projectName}`} /><Row k="Lahan" v={a.land ? `${a.land.landCode} · ${a.areaHectares} ha` : null} />
          <GpsInfo gps={a.gps} testid="activity-gps" />
          {a.gps && <a className="inline-flex items-center gap-1 text-emerald-800 font-semibold" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps?q=${a.gps.latitude},${a.gps.longitude}`} data-testid="activity-map-link"><MapPin className="h-4 w-4" />Buka di peta</a>}
        </Block>
        <Block q="Siapa yang melapor & memverifikasi" testid="block-who">
          <Row k="Dilaporkan oleh (organisasi)" v={a.farmerGroupName} /><Row k="Kolaborasi dengan" v="PT SJI" /><Row k="Petugas pelapor" v={a.submittedByName} />
          <Row k="Dikirim" v={a.submittedAt ? formatDate(a.submittedAt) : "Belum dikirim (draf)"} />
          {a.status === "VERIFIED" ? <p className="flex items-center gap-1.5 text-emerald-800 font-semibold" data-testid="verified-info"><ShieldCheck className="h-4 w-4" />Diverifikasi PT SJI{a.verifiedByName && ` (${a.verifiedByName})`} · {formatDate(a.verifiedAt)}</p>
            : <p className="font-semibold text-amber-800 uppercase">{STATUS_LABELS[a.status]}</p>}
          <Row k="Catatan PT SJI" v={a.verificationNote} />
        </Block>
      </div>
      <Block q={`Bukti (${a.media.length} file)`} testid="block-evidence">
        {groups.length ? groups.map(([label, list]) => (
          <div key={label} className="pt-2">
            <p className="font-semibold text-slate-800 mb-2">{label} ({list.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{list.map((m) => (
              <figure key={m.id} className="min-w-0" data-testid={`evidence-${m.id}`}>
                <div className="aspect-square rounded-lg overflow-hidden border bg-slate-50"><MediaThumb m={m} /></div>
                {m.caption && <figcaption className="mt-1 text-xs text-slate-700 break-words" data-testid={`caption-${m.id}`}>{m.caption}</figcaption>}
                {isManagerRole(user.role) && <CaptionEdit m={m} onSaved={load} />}
              </figure>
            ))}</div>
          </div>
        )) : <p>Belum ada bukti.</p>}
      </Block>
      <AdviceThread a={a} user={user} reload={load} />
      {(a.reviewHistory?.length > 0 || a.revisions?.length > 0) && (
        <Block q="Riwayat verifikasi & revisi" testid="block-history">
          {a.reviewHistory?.map((h, i) => <p key={`r${i}`} className="flex gap-2"><History className="h-4 w-4 mt-0.5 shrink-0" />{formatDate(h.at)} · {STATUS_LABELS[h.status]}{h.note && ` - "${h.note}"`}</p>)}
          {a.revisions?.map((r) => <p key={r.id} className="flex gap-2" data-testid={`revision-${r.revision}`}><FilePenLine className="h-4 w-4 mt-0.5 shrink-0" />Revisi #{r.revision} oleh {r.editedByName} · {formatDate(r.editedAt)} (versi sebelumnya tersimpan)</p>)}
        </Block>
      )}
    </div>
  );
}
